import React from 'react';

import { FeatureTag } from '@components/Forms/FeatureTag';
import { calendarStrings } from '@data/calendar';
import { instructionStrings } from '@data/instruction';
import { moneyStrings } from '@data/money';
import { DeliveryTruck } from '@models/DeliveryInstruction';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    dateMissing: '-',
    priceHintMix: (
      <>
        *日本国内配送に混載トラックを利用した場合の見積合計金額です。含まれている料金項目については別途見積詳細をご確認ください。
      </>
    ),
    priceHintCharter: (
      <>
        *日本国内配送にチャータートラックを利用した場合の見積合計金額です。含まれている料金項目については別途見積詳細をご確認ください。
      </>
    ),
    estimate: '（見込）',
    shipperSide: '（SHIPPER側）',
    consigneeSide: '（CNEE側）',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  deliveryTruckDisplayContainer: {
    ...common.flexColumn,
    height: '100%',
    width: '100%',
    justifyContent: 'start',
    padding: 15,
    paddingRight: 0,
  },
  deliveryTruckDisplayTitle: {
    ...common.biggerText,
    ...common.fontWeight600,
    color: theme.palette.common.black,
    marginBottom: '0.25rem',
  },
  deliveryTruckDisplayLine: {
    ...common.middleText,
    ...common.fontWeight600,
    ...common.textGray700,
  },
  deliveryTruckDisplayPriceHint: {
    ...common.textGray700,
    ...common.smallerText,
    lineHeight: 2,
    marginBottom: 5,
    paddingRight: 60,
  },
  tagsContainer: {
    display: 'inline-flex',
    columnGap: '0.5rem',
    marginLeft: '0.5rem',
    position: 'relative',
    top: -3,
  },
}));

export interface DeliveryTruckDisplayProps {
  item: DeliveryTruck;
}

export const DeliveryTruckDisplay: React.FC<DeliveryTruckDisplayProps> = (props) => {
  const { item } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={classes.deliveryTruckDisplayContainer}>
      <div className={classes.deliveryTruckDisplayTitle}>
        {instructionStrings[item.name]}
        <div className={classes.tagsContainer}>
          {instructionStrings.deliveryOptionTags[item.name].map((item, i) => (
            <FeatureTag key={i} color={item.color}>
              {item.text}
            </FeatureTag>
          ))}
        </div>
      </div>
      {item.name === 'その他' ? (
        <div className={classes.deliveryTruckDisplayLine}>
          {instructionStrings.otherTruckOptions}
        </div>
      ) : (
        <>
          <div className={classes.deliveryTruckDisplayLine}>
            {instructionStrings.deliveryFee}
            {strings.consigneeSide}
            {': '}
            {moneyStrings.formatYen(item.consignee_price)}
          </div>

          <div style={{ height: 4 }} />

          <div className={classes.deliveryTruckDisplayLine}>
            {instructionStrings.deliveryFee}
            {strings.shipperSide}
            {': '}
            {moneyStrings.formatYen(item.shipper_price)}
          </div>

          <div style={{ height: 4 }} />

          {item.name === 'mix' && (
            <div className={classes.deliveryTruckDisplayPriceHint}>{strings.priceHintMix}</div>
          )}

          {item.name !== 'mix' && (
            <div className={classes.deliveryTruckDisplayPriceHint}>{strings.priceHintCharter}</div>
          )}

          <div className={classes.deliveryTruckDisplayLine}>
            {instructionStrings.deliveryDate}
            {strings.estimate}
            {': '}
            {item.delivery_date
              ? calendarStrings.yearMonthDay(new Date(item.delivery_date))
              : strings.dateMissing}
          </div>
        </>
      )}
    </div>
  );
};
